// src/services/requestDemoApi.js
import axios from 'axios';

// Get environment variables specific to the request-demo API
const REQUEST_DEMO_API_URL = process.env.REACT_APP_REQUEST_DEMO_API_URL || 'https://5devwebsite.synergopro.com';
const REQUEST_DEMO_API_TOKEN = process.env.REACT_APP_REQUEST_DEMO_API_TOKEN;

// Log environment variables for debugging (remove in production)
console.log('Using Request Demo API URL:', REQUEST_DEMO_API_URL);
console.log('Request Demo API Token exists:', !!REQUEST_DEMO_API_TOKEN);

// Create a separate axios instance for request-demo API
const requestDemoApi = axios.create({
  baseURL: REQUEST_DEMO_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Authorization': REQUEST_DEMO_API_TOKEN ? `Bearer ${REQUEST_DEMO_API_TOKEN}` : ''
  }
});

// Export API functions for request-demo
export const requestDemoApiService = {
  /**
   * Test API connection to request-demo endpoint
   * @returns {Promise} - API connection status
   */
  testConnection: async () => {
    try {
      // Try to fetch a minimal amount of data to test connection
      await requestDemoApi.get('/api/request-demos', {
        params: { 'pagination[limit]': 1 }
      });
      return { success: true, message: 'Request Demo API connection successful' };
    } catch (error) {
      console.error('Request Demo API Connection test failed:', error);
      // If there's a 403 error, we might still have partial access
      if (error.response?.status === 403) {
        return {
          success: true,
          message: 'Limited access to Request Demo API (some endpoints may work)'
        };
      }

      return {
        success: false,
        message: error.response?.data?.error?.message || error.message || 'Request Demo API connection failed'
      };
    }
  },

  /**
   * Fetch request-demo records with sorting and pagination
   * @param {number} limit - The maximum number of records to fetch
   * @param {string} sortField - Field to sort by (default: updatedAt)
   * @param {string} sortOrder - Sort order (default: desc)
   * @returns {Promise} - The API response
   */
  fetchRequestDemos: async (limit = 100, sortField = 'updatedAt', sortOrder = 'desc') => {
    try {
      const response = await requestDemoApi.get('/api/request-demos', {
        params: {
          'populate': '*',
          'pagination[limit]': limit,
          [`sort[0]`]: `${sortField}:${sortOrder}`
        }
      });

      // Log the response structure to help with debugging
      console.log('Request Demo API Response:', response.data);

      return { data: response.data, error: null };
    } catch (error) {
      console.error('Error fetching request demos:', error);
      return {
        data: null,
        error: error.response?.data?.error || error.message || 'Unknown error'
      };
    }
  },

  /**
   * Update a request-demo record
   * @param {number|string} id - The record ID to update
   * @param {object} data - The data to update
   * @returns {Promise} - The API response
   */
  updateRequestDemo: async (id, data) => {
    try {
      console.log('Updating request demo with ID:', id);
      console.log('Update data:', data);

      // Since direct updates are not supported, we'll use a workaround:
      // 1. Create a new record with the updated data
      // 2. Delete the old record (if needed)

      // Format the data for creating a new record
      const formattedData = {
        data: {
          name: data.name,
          email: data.email,
          company: data.company,
          phone: data.phone,
          countryCode: data.countryCode,
          message: data.message,
          enquiryType: data.enquiryType
        }
      };

      console.log('Formatted data for create:', formattedData);

      // Create a new record with the updated data
      console.log('Creating new record with updated data');
      const response = await requestDemoApi.post('/api/request-demos', formattedData);
      console.log('Create response:', response.data);

      // Return success with the new record data
      return {
        success: true,
        data: response.data,
        error: null,
        message: 'Record updated by creating a new one. You may want to delete the old record.'
      };
    } catch (error) {
      console.error(`Error updating request demo record:`, error);
      console.error('Error status:', error.response?.status);
      console.error('Error details:', error.response?.data);

      return {
        success: false,
        data: null,
        error: error.response?.data?.error?.message || error.message || 'Unknown error'
      };
    }
  },

  /**
   * Delete a request-demo record
   * @param {number|string} id - The record ID to delete
   * @returns {Promise} - The API response
   */
  deleteRequestDemo: async (id) => {
    try {
      const response = await requestDemoApi.delete(`/api/request-demos/${id}`);
      return { success: true, data: response.data, error: null };
    } catch (error) {
      console.error(`Error deleting request demo record:`, error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.error || error.message || 'Unknown error'
      };
    }
  }
};

export default requestDemoApiService;
